import { memo } from "react";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import {
    getAccountNameWithLast4,
    getLeadingAccountConnection,
} from "@common/helpers/financialAccount";
import type { FinancialAccountBase } from "@common/types/financialAccount";
import type { FinancialConnection } from "@common/types/financialConnection";
import type { IntegrationConnection } from "@common/types/integrationConnection";
import { Picker, type PickerItemProps } from "@design-system/Picker/Picker.lib";
import { AccountInstitutionIcon } from "@components/institution/AccountInstitutionIcon";
import { getAccountClassificationLabel } from "./helpers";

export interface AccountPickerItemProps extends Omit<
    PickerItemProps,
    "children" | "leading" | "description"
> {
    account: FinancialAccountBase;
    /** Defaults to the account's leading connection, as `AccountCard` does. */
    connection?: FinancialConnection | IntegrationConnection;
}

/** The 16px connection / institution icon `AccountCard` shows for an account. */
export const AccountPickerIcon = ({
    account,
    connection,
}: Pick<AccountPickerItemProps, "account" | "connection">) => (
    <AccountInstitutionIcon
        account={account}
        connection={connection ?? getLeadingAccountConnection(account)}
        size="xs"
    />
);

/** `AccountCard showNumber`'s second line: the masked number, else Business / Personal. */
export function getAccountPickerDescription(
    account: FinancialAccountBase,
): string {
    return account.accountNumberMask
        ? getAnonymousAccountNumber(account.accountNumberMask)
        : getAccountClassificationLabel(account);
}

/**
 * Two-line account row (`AccountCard showNumber` in a picker): icon, account
 * name, and the masked number or classification below it. `value` defaults
 * to the account id.
 */
export const AccountPickerItem = memo(
    ({ account, connection, ...itemProps }: AccountPickerItemProps) => (
        <Picker.Item
            value={account.id}
            {...itemProps}
            leading={
                <AccountPickerIcon account={account} connection={connection} />
            }
            description={getAccountPickerDescription(account)}
        >
            {account.name ?? ""}
        </Picker.Item>
    ),
);
AccountPickerItem.displayName = "AccountPickerItem";

/**
 * Single-line account row (`AccountCard numberInName` in a picker): icon,
 * name and the masked last four, unless the name already ends with them.
 */
export const AccountNumberPickerItem = memo(
    ({ account, connection, ...itemProps }: AccountPickerItemProps) => {
        const name = account.name ?? "";
        const mask = account.accountNumberMask;
        const icon = (
            <AccountPickerIcon account={account} connection={connection} />
        );

        if (!mask || getAccountNameWithLast4(account) === name) {
            return (
                <Picker.Item value={account.id} {...itemProps} leading={icon}>
                    {name}
                </Picker.Item>
            );
        }

        return (
            <Picker.AccountItem
                value={account.id}
                {...itemProps}
                icon={icon}
                name={name}
                mask={getAnonymousAccountNumber(mask, 0)}
            />
        );
    },
);
AccountNumberPickerItem.displayName = "AccountNumberPickerItem";
